import React, { useState, useCallback } from 'react';
import {
  Scissors,
  MessageSquareQuote,
  Plus,
  Trash2,
  Save,
  CheckCircle,
  Loader2,
  GripVertical,
  ChevronUp,
  ChevronDown,
  X,
  Edit2,
} from 'lucide-react';
import type { PrioritizationSession } from '../../../types/prioritization/session';
import type {
  NestedPrioritizationEvent,
  NestedPrioritizationCutLineEvent,
  NestedPrioritizationQAEvent,
  NestedPrioritizationQuestion,
  NestedPrioritizationAnswer,
  NestedPrioritizationResponseType,
} from '../../../types/prioritization/nestedPrioritizationEvent';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { useSessionStore } from '../../../stores/prioritization/sessionStore';
import { useUserStore } from '../../../stores/prioritization/userStore';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function newId(): string {
  return crypto.randomUUID();
}

function nowIso(): string {
  return new Date().toISOString();
}

// ─── Subject label resolver ───────────────────────────────────────────────────

function useSubjectLabel(session: PrioritizationSession) {
  const items = useItemsStore(s => s.items);
  return useCallback(
    (id: string): string => {
      // Isolated items are plain strings stored as IDs
      if (session.sessionContextMode === 'isolated') return id;
      const item = items.find(i => i.id === id);
      return item?.title ?? id;
    },
    [items, session.sessionContextMode],
  );
}

// ─── Derive default subjects for a new cut-line event ─────────────────────────

function useDefaultSubjects(session: PrioritizationSession): string[] {
  const items = useItemsStore(s => s.items);

  if (session.sessionContextMode === 'isolated') {
    return session.isolatedItems ?? [];
  }
  // Use session item IDs if present, else fall back to items in the store
  if (session.itemIds?.length) return session.itemIds;
  return items.slice(0, 20).map(i => i.id);
}

// ─── Mode selector ────────────────────────────────────────────────────────────

function ModeSelectorCard({ onSelect }: { onSelect: (mode: 'cut_line' | 'question_answers') => void }) {
  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold text-[var(--text-pri)]">Add a prioritization event</h3>
        <p className="mt-1 text-sm text-[var(--text-ter)]">
          Embed a lightweight decision record inside this session — without running a full S6 ranking.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Cut line */}
        <button
          type="button"
          onClick={() => onSelect('cut_line')}
          className="group text-left p-5 bg-[var(--bg-raised)] border-2 border-[var(--rule)] hover:border-[var(--slate)] hover:bg-[var(--slate-dim)] transition-colors"
        >
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex-shrink-0 w-9 h-9 bg-[var(--slate-dim)] group-hover:bg-[var(--slate-dim)] flex items-center justify-center transition-colors">
              <Scissors className="h-4 w-4 text-[var(--text-ter)]" />
            </div>
            <div>
              <p className="text-sm font-semibold text-[var(--text-pri)]">Cut line</p>
              <p className="mt-0.5 text-xs text-[var(--text-ter)] leading-relaxed">
                Order items and draw a cut line. Items above are&nbsp;<strong>in</strong>; items below are&nbsp;<strong>out</strong>.
              </p>
            </div>
          </div>
        </button>

        {/* Questions & Answers */}
        <button
          type="button"
          onClick={() => onSelect('question_answers')}
          className="group text-left p-5 bg-[var(--bg-raised)] border-2 border-[var(--rule)] hover:border-[var(--slate)] hover:bg-[var(--slate-dim)] transition-colors"
        >
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex-shrink-0 w-9 h-9 bg-[var(--slate-dim)] group-hover:bg-[var(--slate-dim)] flex items-center justify-center transition-colors">
              <MessageSquareQuote className="h-4 w-4 text-[var(--text-ter)]" />
            </div>
            <div>
              <p className="text-sm font-semibold text-[var(--text-pri)]">Questions &amp; answers</p>
              <p className="mt-0.5 text-xs text-[var(--text-ter)] leading-relaxed">
                Define specific questions and record the group&rsquo;s answers as the decision.
              </p>
            </div>
          </div>
        </button>
      </div>
    </div>
  );
}

// ─── Cut-line editor ──────────────────────────────────────────────────────────

interface CutLineEditorProps {
  initial: NestedPrioritizationCutLineEvent | null;
  defaultSubjects: string[];
  getLabel: (id: string) => string;
  onSave: (event: NestedPrioritizationCutLineEvent) => Promise<void>;
  onDelete?: () => void;
}

function CutLineEditor({ initial, defaultSubjects, getLabel, onSave, onDelete }: CutLineEditorProps) {
  const [label, setLabel] = useState(initial?.label ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [rationale, setRationale] = useState(initial?.rationale ?? '');
  const [subjects, setSubjects] = useState<string[]>(
    initial?.rankedSubjectIds?.length ? initial.rankedSubjectIds : defaultSubjects,
  );
  const [cutLine, setCutLine] = useState<number>(
    initial?.cutLinePosition ?? Math.ceil((initial?.rankedSubjectIds?.length ?? defaultSubjects.length) / 2),
  );
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const currentUserId = useUserStore(s => s.userId);

  const moveUp = useCallback((idx: number) => {
    if (idx === 0) return;
    setSubjects(prev => {
      const next = [...prev];
      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      return next;
    });
    setSaved(false);
  }, []);

  const moveDown = useCallback((idx: number) => {
    setSubjects(prev => {
      if (idx >= prev.length - 1) return prev;
      const next = [...prev];
      [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
      return next;
    });
    setSaved(false);
  }, []);

  const moveCutLineUp = () => setCutLine(c => Math.max(0, c - 1));
  const moveCutLineDown = () => setCutLine(c => Math.min(subjects.length, c + 1));

  const handleSave = async () => {
    setSaving(true);
    const event: NestedPrioritizationCutLineEvent = {
      id: initial?.id ?? newId(),
      mode: 'cut_line',
      label: label.trim() || undefined,
      description: description.trim() || undefined,
      rationale: rationale.trim() || undefined,
      rankedSubjectIds: subjects,
      cutLinePosition: cutLine,
      setBy: currentUserId,
      setAt: nowIso(),
    };
    await onSave(event);
    setSaving(false);
    setSaved(true);
  };

  const aboveCount = cutLine;
  const belowCount = subjects.length - cutLine;

  return (
    <div className="space-y-5">
      {/* Label + description */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">Event label (optional)</label>
          <input
            type="text"
            value={label}
            onChange={e => { setLabel(e.target.value); setSaved(false); }}
            placeholder="e.g. Which criteria make the cut?"
            className="w-full px-3 py-2 text-sm border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-sky-300"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">Description (optional)</label>
          <input
            type="text"
            value={description}
            onChange={e => { setDescription(e.target.value); setSaved(false); }}
            placeholder="Short context for participants"
            className="w-full px-3 py-2 text-sm border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-sky-300"
          />
        </div>
      </div>

      {/* Ordered list with cut line */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-medium text-[var(--text-sec)] uppercase tracking-wide">
            Item order &amp; cut line
          </p>
          <div className="flex items-center gap-2">
            <span className="text-xs text-[var(--text-ter)]">
              {aboveCount} in · {belowCount} out
            </span>
            <button
              type="button"
              onClick={moveCutLineUp}
              disabled={cutLine === 0}
              title="Move cut line up"
              className="p-1 rounded hover:bg-[var(--hover-tint)] disabled:opacity-30 transition-colors"
            >
              <ChevronUp className="h-3.5 w-3.5 text-[var(--text-ter)]" />
            </button>
            <button
              type="button"
              onClick={moveCutLineDown}
              disabled={cutLine === subjects.length}
              title="Move cut line down"
              className="p-1 rounded hover:bg-[var(--hover-tint)] disabled:opacity-30 transition-colors"
            >
              <ChevronDown className="h-3.5 w-3.5 text-[var(--text-ter)]" />
            </button>
          </div>
        </div>

        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden divide-y divide-[var(--rule)]">
          {subjects.length === 0 && (
            <p className="px-4 py-8 text-center text-sm text-[var(--text-ter)]">
              No items to arrange. Add items to the session first.
            </p>
          )}
          {subjects.map((id, idx) => {
            const isAbove = idx < cutLine;
            const isCutLineHere = idx === cutLine;
            return (
              <React.Fragment key={id}>
                {isCutLineHere && (
                  <div className="px-4 py-2 bg-[var(--mustard-dim)] border-y border-[var(--mustard)] flex items-center gap-2">
                    <div className="flex-1 h-px bg-[var(--mustard)]" />
                    <div className="flex items-center gap-1.5">
                      <Scissors className="h-3 w-3 text-[var(--mustard)]" />
                      <span className="text-xs font-semibold text-[var(--mustard)] uppercase tracking-wide">Cut line</span>
                    </div>
                    <div className="flex-1 h-px bg-[var(--mustard)]" />
                  </div>
                )}
                <div
                  className={[
                    'flex items-center gap-3 px-4 py-3 transition-colors',
                    isAbove
                      ? 'bg-[var(--slate-dim)]/60 hover:bg-[var(--slate-dim)]'
                      : 'opacity-60 hover:opacity-80',
                  ].join(' ')}
                >
                  <GripVertical className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0" />
                  <span
                    className={[
                      'flex-shrink-0 w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center',
                      isAbove
                        ? 'bg-[var(--slate)] text-white'
                        : 'bg-[var(--hover-tint)] text-[var(--text-ter)]',
                    ].join(' ')}
                  >
                    {idx + 1}
                  </span>
                  <span className="flex-1 text-sm text-[var(--text-pri)] truncate">{getLabel(id)}</span>
                  {isAbove && (
                    <span className="flex-shrink-0 text-xs font-medium text-[var(--text-sec)] bg-[var(--slate-dim)] rounded-full px-2 py-0.5">
                      In
                    </span>
                  )}
                  <div className="flex-shrink-0 flex items-center gap-0.5">
                    <button
                      type="button"
                      onClick={() => moveUp(idx)}
                      disabled={idx === 0}
                      className="p-1 rounded hover:bg-[var(--hover-tint)] disabled:opacity-30 transition-colors"
                      title="Move up"
                    >
                      <ChevronUp className="h-3.5 w-3.5 text-[var(--text-ter)]" />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveDown(idx)}
                      disabled={idx === subjects.length - 1}
                      className="p-1 rounded hover:bg-[var(--hover-tint)] disabled:opacity-30 transition-colors"
                      title="Move down"
                    >
                      <ChevronDown className="h-3.5 w-3.5 text-[var(--text-ter)]" />
                    </button>
                  </div>
                </div>
              </React.Fragment>
            );
          })}

          {/* Cut line at bottom — all items above */}
          {cutLine === subjects.length && subjects.length > 0 && (
            <div className="px-4 py-2 bg-[var(--mustard-dim)] border-t border-[var(--mustard)] flex items-center gap-2">
              <div className="flex-1 h-px bg-[var(--mustard)]" />
              <div className="flex items-center gap-1.5">
                <Scissors className="h-3 w-3 text-[var(--mustard)]" />
                <span className="text-xs font-semibold text-[var(--mustard)] uppercase tracking-wide">Cut line (all included)</span>
              </div>
              <div className="flex-1 h-px bg-[var(--mustard)]" />
            </div>
          )}
        </div>
      </div>

      {/* Rationale */}
      <div>
        <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">Rationale (optional)</label>
        <textarea
          value={rationale}
          onChange={e => { setRationale(e.target.value); setSaved(false); }}
          placeholder="Why was the cut line placed here?"
          rows={2}
          className="w-full px-3 py-2 text-sm border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-sky-300 resize-none"
        />
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between">
        <div>
          {onDelete && (
            <button
              type="button"
              onClick={onDelete}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-[var(--coral)] hover:bg-[var(--coral-dim)] transition-colors"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Remove event
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || subjects.length === 0}
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-[var(--slate)] hover:bg-[var(--slate)] disabled:opacity-50 transition-colors"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : saved ? <CheckCircle className="h-3.5 w-3.5" /> : <Save className="h-3.5 w-3.5" />}
          {saving ? 'Saving…' : 'Save cut line'}
        </button>
      </div>
    </div>
  );
}

// ─── Q&A editor ───────────────────────────────────────────────────────────────

const RESPONSE_TYPE_LABELS: Record<NestedPrioritizationResponseType, string> = {
  yes_no: 'Yes / No',
  select_one: 'Select one',
  select_many: 'Select many',
  free_text: 'Free text',
};

interface QAEditorProps {
  initial: NestedPrioritizationQAEvent | null;
  onSave: (event: NestedPrioritizationQAEvent) => Promise<void>;
  onDelete?: () => void;
}

function QAEditor({ initial, onSave, onDelete }: QAEditorProps) {
  const currentUserId = useUserStore(s => s.userId);
  const [label, setLabel] = useState(initial?.label ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [questions, setQuestions] = useState<NestedPrioritizationQuestion[]>(
    initial?.questions ?? [],
  );
  const [answers, setAnswers] = useState<NestedPrioritizationAnswer[]>(
    initial?.answers ?? [],
  );
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [newQText, setNewQText] = useState('');
  const [newQType, setNewQType] = useState<NestedPrioritizationResponseType>('yes_no');
  const [newQOptions, setNewQOptions] = useState('');
  const [addingQuestion, setAddingQuestion] = useState(false);

  const addQuestion = () => {
    if (!newQText.trim()) return;
    const q: NestedPrioritizationQuestion = {
      id: newId(),
      text: newQText.trim(),
      responseType: newQType,
      options: (newQType === 'select_one' || newQType === 'select_many')
        ? newQOptions.split(',').map(o => o.trim()).filter(Boolean)
        : undefined,
      required: true,
    };
    setQuestions(prev => [...prev, q]);
    setNewQText('');
    setNewQOptions('');
    setNewQType('yes_no');
    setAddingQuestion(false);
    setSaved(false);
  };

  const removeQuestion = (id: string) => {
    setQuestions(prev => prev.filter(q => q.id !== id));
    setAnswers(prev => prev.filter(a => a.questionId !== id));
    setSaved(false);
  };

  const getAnswer = (qId: string): NestedPrioritizationAnswer | undefined =>
    answers.find(a => a.questionId === qId);

  const setAnswer = (qId: string, value: boolean | string | string[]) => {
    const ts = nowIso();
    setAnswers(prev => {
      const existing = prev.findIndex(a => a.questionId === qId);
      const next: NestedPrioritizationAnswer = {
        questionId: qId,
        value,
        answeredBy: currentUserId,
        answeredAt: ts,
      };
      if (existing >= 0) {
        return prev.map((a, i) => (i === existing ? next : a));
      }
      return [...prev, next];
    });
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    const event: NestedPrioritizationQAEvent = {
      id: initial?.id ?? newId(),
      mode: 'question_answers',
      label: label.trim() || undefined,
      description: description.trim() || undefined,
      questions,
      answers,
    };
    await onSave(event);
    setSaving(false);
    setSaved(true);
  };

  return (
    <div className="space-y-5">
      {/* Label + description */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">Event label (optional)</label>
          <input
            type="text"
            value={label}
            onChange={e => { setLabel(e.target.value); setSaved(false); }}
            placeholder="e.g. Alignment check for criteria"
            className="w-full px-3 py-2 text-sm border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-violet-300"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">Description (optional)</label>
          <input
            type="text"
            value={description}
            onChange={e => { setDescription(e.target.value); setSaved(false); }}
            placeholder="What this Q&A is deciding"
            className="w-full px-3 py-2 text-sm border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-violet-300"
          />
        </div>
      </div>

      {/* Questions */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-medium text-[var(--text-sec)] uppercase tracking-wide">Questions</p>
          <button
            type="button"
            onClick={() => setAddingQuestion(true)}
            className="inline-flex items-center gap-1 text-xs font-medium text-[var(--text-ter)] hover:text-[var(--text-ter)] transition-colors"
          >
            <Plus className="h-3.5 w-3.5" />
            Add question
          </button>
        </div>

        <div className="space-y-3">
          {questions.length === 0 && !addingQuestion && (
            <div className="px-4 py-6 text-center bg-[var(--bg-raised)] border border-dashed border-[var(--rule)]">
              <MessageSquareQuote className="h-8 w-8 text-[var(--text-ter)] mx-auto mb-2" />
              <p className="text-sm text-[var(--text-ter)]">No questions yet. Add one to get started.</p>
            </div>
          )}

          {questions.map((q, idx) => {
            const answer = getAnswer(q.id);
            return (
              <div key={q.id} className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden">
                {/* Question header */}
                <div className="px-4 py-3 bg-[var(--surface)] border-b border-[var(--rule)] flex items-start gap-2">
                  <span className="flex-shrink-0 mt-0.5 w-5 h-5 rounded-full bg-[var(--slate-dim)] text-[var(--text-sec)] text-xs font-bold flex items-center justify-center">
                    {idx + 1}
                  </span>
                  <p className="flex-1 text-sm font-medium text-[var(--text-pri)]">{q.text}</p>
                  <span className="flex-shrink-0 text-xs text-[var(--text-ter)] border border-[var(--rule)] rounded px-1.5 py-0.5">
                    {RESPONSE_TYPE_LABELS[q.responseType]}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeQuestion(q.id)}
                    className="flex-shrink-0 p-1 text-[var(--text-ter)] hover:text-[var(--coral)] rounded transition-colors"
                    title="Remove question"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>

                {/* Answer input */}
                <div className="px-4 py-3">
                  {q.responseType === 'yes_no' && (
                    <div className="flex items-center gap-2">
                      {(['Yes', 'No'] as const).map(opt => (
                        <button
                          key={opt}
                          type="button"
                          onClick={() => setAnswer(q.id, opt === 'Yes')}
                          className={[
                            'px-3 py-1.5 text-sm font-medium  border transition-colors',
                            answer?.value === (opt === 'Yes')
                              ? 'bg-[var(--slate)] text-white border-[var(--slate)]'
                              : 'bg-[var(--bg-raised)] text-[var(--text-sec)] border-[var(--rule)] hover:border-[var(--slate)]',
                          ].join(' ')}
                        >
                          {opt}
                        </button>
                      ))}
                      {answer !== undefined && (
                        <span className="ml-2 text-xs text-[var(--text-ter)]">
                          Answered
                        </span>
                      )}
                    </div>
                  )}

                  {q.responseType === 'select_one' && (
                    <div className="flex flex-wrap gap-2">
                      {(q.options ?? []).map(opt => (
                        <button
                          key={opt}
                          type="button"
                          onClick={() => setAnswer(q.id, opt)}
                          className={[
                            'px-3 py-1.5 text-sm font-medium  border transition-colors',
                            answer?.value === opt
                              ? 'bg-[var(--slate)] text-white border-[var(--slate)]'
                              : 'bg-[var(--bg-raised)] text-[var(--text-sec)] border-[var(--rule)] hover:border-[var(--slate)]',
                          ].join(' ')}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  )}

                  {q.responseType === 'select_many' && (
                    <div className="flex flex-wrap gap-2">
                      {(q.options ?? []).map(opt => {
                        const selected = Array.isArray(answer?.value) && answer.value.includes(opt);
                        return (
                          <button
                            key={opt}
                            type="button"
                            onClick={() => {
                              const current = Array.isArray(answer?.value) ? [...answer.value] : [];
                              const next = selected
                                ? current.filter(v => v !== opt)
                                : [...current, opt];
                              setAnswer(q.id, next);
                            }}
                            className={[
                              'px-3 py-1.5 text-sm font-medium  border transition-colors',
                              selected
                                ? 'bg-[var(--slate)] text-white border-[var(--slate)]'
                                : 'bg-[var(--bg-raised)] text-[var(--text-sec)] border-[var(--rule)] hover:border-[var(--slate)]',
                            ].join(' ')}
                          >
                            {opt}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {q.responseType === 'free_text' && (
                    <textarea
                      value={typeof answer?.value === 'string' ? answer.value : ''}
                      onChange={e => setAnswer(q.id, e.target.value)}
                      placeholder="Enter answer…"
                      rows={2}
                      className="w-full px-3 py-2 text-sm border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-violet-300 resize-none"
                    />
                  )}
                </div>
              </div>
            );
          })}

          {/* Add question form */}
          {addingQuestion && (
            <div className="bg-[var(--slate-dim)] border border-[var(--slate)] p-4 space-y-3">
              <p className="text-xs font-semibold text-[var(--text-sec)] uppercase tracking-wide">New question</p>
              <input
                type="text"
                value={newQText}
                onChange={e => setNewQText(e.target.value)}
                placeholder="Question text…"
                className="w-full px-3 py-2 text-sm border border-[var(--slate)] bg-[var(--bg-raised)] focus:outline-none focus:ring-2 focus:ring-violet-300"
                autoFocus
                onKeyDown={e => { if (e.key === 'Enter') addQuestion(); if (e.key === 'Escape') setAddingQuestion(false); }}
              />
              <div className="flex items-center gap-3">
                <select
                  value={newQType}
                  onChange={e => setNewQType(e.target.value as NestedPrioritizationResponseType)}
                  className="px-2 py-1.5 text-sm border border-[var(--slate)] bg-[var(--bg-raised)] focus:outline-none focus:ring-2 focus:ring-violet-300"
                >
                  {(Object.entries(RESPONSE_TYPE_LABELS) as [NestedPrioritizationResponseType, string][]).map(
                    ([val, lbl]) => <option key={val} value={val}>{lbl}</option>
                  )}
                </select>
                {(newQType === 'select_one' || newQType === 'select_many') && (
                  <input
                    type="text"
                    value={newQOptions}
                    onChange={e => setNewQOptions(e.target.value)}
                    placeholder="Option A, Option B, Option C"
                    className="flex-1 px-3 py-1.5 text-sm border border-[var(--slate)] bg-[var(--bg-raised)] focus:outline-none focus:ring-2 focus:ring-violet-300"
                  />
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={addQuestion}
                  disabled={!newQText.trim()}
                  className="px-3 py-1.5 text-sm font-medium text-white bg-[var(--slate)] hover:bg-[var(--slate)] disabled:opacity-50 transition-colors"
                >
                  Add
                </button>
                <button
                  type="button"
                  onClick={() => setAddingQuestion(false)}
                  className="px-3 py-1.5 text-sm text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between">
        <div>
          {onDelete && (
            <button
              type="button"
              onClick={onDelete}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-[var(--coral)] hover:bg-[var(--coral-dim)] transition-colors"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Remove event
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || questions.length === 0}
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-[var(--slate)] hover:bg-[var(--slate)] disabled:opacity-50 transition-colors"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : saved ? <CheckCircle className="h-3.5 w-3.5" /> : <Save className="h-3.5 w-3.5" />}
          {saving ? 'Saving…' : 'Save answers'}
        </button>
      </div>
    </div>
  );
}

// ─── View: existing event summary ─────────────────────────────────────────────

function EventSummaryHeader({
  event,
  onEdit,
}: {
  event: NestedPrioritizationEvent;
  onEdit: () => void;
}) {
  const isCutLine = event.mode === 'cut_line';
  const colorClass = isCutLine ? 'sky' : 'violet';
  const Icon = isCutLine ? Scissors : MessageSquareQuote;
  const modeLabel = isCutLine ? 'Cut line event' : 'Q&A event';

  return (
    <div
      className={`flex items-start justify-between gap-3 p-4  border-2 border-${colorClass}-200 bg-${colorClass}-50`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`flex-shrink-0 w-8 h-8  bg-${colorClass}-100 flex items-center justify-center`}
        >
          <Icon className={`h-4 w-4 text-${colorClass}-600`} />
        </div>
        <div>
          <p className={`text-sm font-semibold text-${colorClass}-900`}>
            {event.label || modeLabel}
          </p>
          {event.description && (
            <p className="mt-0.5 text-xs text-[var(--text-ter)]">{event.description}</p>
          )}
          {isCutLine && (
            <p className="mt-1 text-xs text-[var(--text-ter)]">
              {(event as NestedPrioritizationCutLineEvent).cutLinePosition} of{' '}
              {(event as NestedPrioritizationCutLineEvent).rankedSubjectIds.length} items above the cut
            </p>
          )}
          {!isCutLine && (
            <p className="mt-1 text-xs text-[var(--text-ter)]">
              {(event as NestedPrioritizationQAEvent).questions.length} question
              {(event as NestedPrioritizationQAEvent).questions.length !== 1 ? 's' : ''} ·{' '}
              {(event as NestedPrioritizationQAEvent).answers.length} answered
            </p>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={onEdit}
        className={`flex-shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-${colorClass}-700 border border-${colorClass}-300 bg-[var(--bg-raised)]  hover:bg-${colorClass}-50 transition-colors`}
      >
        <Edit2 className="h-3 w-3" />
        Edit
      </button>
    </div>
  );
}

// ─── Main panel ───────────────────────────────────────────────────────────────

interface NestedPrioritizationEventPanelProps {
  session: PrioritizationSession;
}

export function NestedPrioritizationEventPanel({ session }: NestedPrioritizationEventPanelProps) {
  const updateSession = useSessionStore(s => s.updateSession);
  const defaultSubjects = useDefaultSubjects(session);
  const getLabel = useSubjectLabel(session);

  const existing = session.nestedPrioritizationEvent ?? null;

  const [mode, setMode] = useState<'cut_line' | 'question_answers' | null>(
    existing?.mode ?? null,
  );
  const [isEditing, setIsEditing] = useState(!existing);

  const handleModeSelect = (m: 'cut_line' | 'question_answers') => {
    setMode(m);
    setIsEditing(true);
  };

  const handleSave = async (event: NestedPrioritizationEvent) => {
    await updateSession(session.id, { nestedPrioritizationEvent: event });
    setIsEditing(false);
  };

  const handleDelete = async () => {
    await updateSession(session.id, { nestedPrioritizationEvent: undefined });
    setMode(null);
    setIsEditing(true);
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-base font-semibold text-[var(--text-pri)]">Prioritization event</h3>
        <p className="mt-1 text-sm text-[var(--text-ter)]">
          Capture a lightweight prioritization decision inside this session — a cut line or
          a set of answered questions — without running a full S6 force-rank.
        </p>
      </div>

      {/* Existing event summary (when not editing) */}
      {existing && !isEditing && (
        <EventSummaryHeader event={existing} onEdit={() => setIsEditing(true)} />
      )}

      {/* No event yet — show mode selector */}
      {!existing && !mode && (
        <ModeSelectorCard onSelect={handleModeSelect} />
      )}

      {/* Editors */}
      {isEditing && mode === 'cut_line' && (
        <div className="bg-[var(--bg-raised)] border border-[var(--slate)] p-5">
          <div className="flex items-center gap-2 mb-5">
            <Scissors className="h-4 w-4 text-[var(--text-ter)]" />
            <h4 className="text-sm font-semibold text-[var(--text-pri)]">Cut line event</h4>
            {existing && (
              <button
                type="button"
                onClick={() => setIsEditing(false)}
                className="ml-auto p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)] rounded transition-colors"
                title="Cancel editing"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <CutLineEditor
            initial={existing?.mode === 'cut_line' ? existing : null}
            defaultSubjects={defaultSubjects}
            getLabel={getLabel}
            onSave={handleSave}
            onDelete={existing ? handleDelete : undefined}
          />
        </div>
      )}

      {isEditing && mode === 'question_answers' && (
        <div className="bg-[var(--bg-raised)] border border-[var(--slate)] p-5">
          <div className="flex items-center gap-2 mb-5">
            <MessageSquareQuote className="h-4 w-4 text-[var(--text-ter)]" />
            <h4 className="text-sm font-semibold text-[var(--text-pri)]">Questions &amp; answers event</h4>
            {existing && (
              <button
                type="button"
                onClick={() => setIsEditing(false)}
                className="ml-auto p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)] rounded transition-colors"
                title="Cancel editing"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <QAEditor
            initial={existing?.mode === 'question_answers' ? existing : null}
            onSave={handleSave}
            onDelete={existing ? handleDelete : undefined}
          />
        </div>
      )}

      {/* Switch mode option when creating (no existing event) */}
      {!existing && mode && !isEditing && (
        <button
          type="button"
          onClick={() => { setMode(null); setIsEditing(false); }}
          className="text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)] transition-colors"
        >
          ← Choose a different type
        </button>
      )}
      {!existing && mode && isEditing && (
        <button
          type="button"
          onClick={() => { setMode(null); setIsEditing(false); }}
          className="text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)] transition-colors"
        >
          ← Choose a different type
        </button>
      )}
    </div>
  );
}
